import React, { useRef, useState } from "react";

/**
 * Button with magnetic hover effect - follows cursor within bounds
 * @param {Object} props
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.href - Optional link URL
 * @param {function} props.onClick - Click handler
 * @param {number} props.strength - Magnetic pull strength (default: 0.3)
 */
const MagneticButton = ({
  children,
  className = "",
  href,
  onClick,
  strength = 0.3,
  ...props
}) => {
  const buttonRef = useRef(null);
  const [transform, setTransform] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const deltaX = (e.clientX - centerX) * strength;
    const deltaY = (e.clientY - centerY) * strength;

    setTransform({ x: deltaX, y: deltaY });
  };

  const handleMouseLeave = () => {
    setTransform({ x: 0, y: 0 });
  };

  const baseClasses = `
    relative inline-flex items-center justify-center
    px-8 py-4
    bg-pine-800
    text-cloud-50 font-semibold text-lg
    rounded-full
    transition-all duration-300 ease-out
    hover:shadow-lg hover:shadow-pine-900/30
    hover:scale-105
    active:scale-95
    focus:outline-none focus:ring-2 focus:ring-moss-500 focus:ring-offset-2
  `;

  const style = {
    transform: `translate(${transform.x}px, ${transform.y}px)`,
    transition: transform.x === 0 && transform.y === 0 ? "transform 0.3s ease-out" : "none",
  };

  const sharedProps = {
    ref: buttonRef,
    className: `${baseClasses} ${className}`,
    style,
    onMouseMove: handleMouseMove,
    onMouseLeave: handleMouseLeave,
    ...props,
  };

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...sharedProps}>
        {children}
      </a>
    );
  }

  return (
    <button onClick={onClick} {...sharedProps}>
      {children}
    </button>
  );
};

export default MagneticButton;
